import { db, Operation } from './db';
import { isEqual } from 'lodash';
import { logger } from '../utils/logger';

const PROCESS_QUEUE_MESSAGE = 'processQueue';

export async function enqueueOperation(
  type: Operation['type'],
  payload: any,
  priority: number = 100
): Promise<void> {
  // We create a deterministic string representation of the payload to use as a hash.
  const payloadHash = JSON.stringify(payload);

  // Early exit: if a bulk generateEmbedding op (payload = {}) is already pending or
  // processing, any URL-specific op is redundant — the bulk sweep covers ALL pages
  // without embeddings, including this URL. This prevents unbounded accumulation of
  // individual URL ops when the user makes rapid tag changes while a bulk sweep is queued.
  if (type === 'generateEmbedding' && (payload?.url || payload?.urls)) {
    const bulkHash = JSON.stringify({});
    const bulkPendingCount = await db.operations
      .where({ type: 'generateEmbedding', payloadHash: bulkHash })
      .filter((op) => op.status === 'pending' || op.status === 'processing')
      .count();
    if (bulkPendingCount > 0) {
      logger.info('[Queue] Skipping URL-specific generateEmbedding: bulk sweep already pending', payload);
      return;
    }
  }

  // Check for duplicates across the ENTIRE queue using the payloadHash index
  const pendingOpsCount = await db.operations
    .where({ type, payloadHash })
    .filter((op) => op.status === 'pending' || op.status === 'processing')
    .count();

  if (pendingOpsCount > 0) {
    // Elevated to info for embedding ops so dedup events are visible in the persisted log,
    // helping diagnose why repeated enqueues happen despite an op already being pending/processing.
    if (type === 'generateEmbedding') {
      logger.info(`[Queue] Skipping duplicate generateEmbedding op (already pending/processing)`, payload);
    } else {
      logger.debug(`[Queue] Skipping duplicate operation: ${type}`, payload);
    }
    return;
  }

  // Elevated to info for embedding ops so every new creation is visible in the persisted log —
  // this is the key signal for diagnosing "why does a new op keep appearing after completion".
  if (type === 'generateEmbedding') {
    logger.info(`[Queue] Enqueuing generateEmbedding op`, payload);
  } else {
    logger.debug(`[Queue] Enqueuing operation: ${type}`, payload);
  }
  await db.operations.add({
    type,
    payload,
    payloadHash,
    status: 'pending',
    createdAt: Date.now(),
    priority,
  });

  // Wake up the service worker to process the queue.
  // Use chrome.runtime.sendMessage directly (MV3 returns a Promise when no callback is given).
  // Guard with typeof check so this is safe to import in Web Worker contexts (where chrome may
  // not be injected), even though enqueueOperation is never called from the worker.
  try {
    if (typeof chrome !== 'undefined' && chrome.runtime?.sendMessage) {
      await chrome.runtime.sendMessage(PROCESS_QUEUE_MESSAGE);
    }
  } catch (e) {
    // Elevated to warn: if sendMessage fails, the op sits in the DB without a processQueue trigger.
    // It will be picked up on the next SW restart, but this is the most likely explanation for
    // "pending ops that never get processed in the same session".
    logger.warn('[Queue] Failed to notify background worker after enqueue — op will be picked up on next SW restart:', e);
  }
}

export async function clearQueue(): Promise<void> {
  logger.info('[Queue] Clearing queue of all operations');
  // We can't immediately abort currently executing promises in JS without an AbortController
  // but we can clear the pending operations so the queue processor will stop.
  const opsToClear = await db.operations
    .filter((op) => op.status === 'pending' || op.status === 'processing')
    .toArray();
    
  if (opsToClear.length > 0) {
    const ids = opsToClear.map(op => op.id).filter(id => id !== undefined) as number[];
    // Chunk to prevent "Maximum call stack size exceeded" in Dexie's internal query expansion
    const batchSize = 100;
    for (let i = 0; i < ids.length; i += batchSize) {
      await db.operations.bulkDelete(ids.slice(i, i + batchSize));
    }
  }
}
